import { Box } from "@mui/material";
import differenceInCalendarDays from "date-fns/differenceInCalendarDays";

import Flx from "../../layout/Flx";
import { slate } from "../../../theme/colors";
import {
  convertToEasternTime,
  formatDateTime,
  normalizeIsoStringToUtc,
} from "../../../_src_shared/utils/functions/formatDateTime";

// currentStatusStartDate is a TIMESTAMP: the backend normalizes every
// milestoneChangeDate to naive UTC and serializes it without an offset
// ("2026-09-18T18:28:40"). normalizeIsoStringToUtc reads it as UTC, and
// convertToEasternTime returns a Date whose local fields are the Eastern wall
// clock — so every day here is an Eastern calendar day, the same for every
// viewer, never the browser's zone.
const easternOf = (iso) =>
  iso ? convertToEasternTime(normalizeIsoStringToUtc(String(iso))) : null;

/**
 * Eastern calendar days since the loan entered its current status:
 * "Today" / "1 day" / "N days". Null / unparseable → "".
 */
export const statusStartDaysLabel = (iso) => {
  const start = easternOf(iso);
  const now = convertToEasternTime(new Date().toISOString());
  if (!start || !now) return "";
  const days = Math.max(differenceInCalendarDays(now, start), 0);
  if (days === 0) return "Today";
  return days === 1 ? "1 day" : `${days} days`;
};

/**
 * Date-filter params for the column. The shared dateTime type compares with
 * `new Date(cellValue)`, which reads the naive UTC string in the VIEWER's zone;
 * this compares the picked day against the timestamp's Eastern calendar day.
 */
export const statusStartDateFilterParams = {
  comparator: (filterLocalDateAtMidnight, cellValue) => {
    const cellDay = easternOf(cellValue);
    if (!cellDay) return 0;
    return Math.sign(
      differenceInCalendarDays(cellDay, filterLocalDateAtMidnight),
    );
  },
};

// "Current Status Start Date" cell: the full Eastern date/time, then the days
// in status in a lighter "(N days)". The column's value stays the timestamp, so
// sort / filter / export still work on the date.
const CellRendererStatusStartDate = (params) => {
  if (params?.node?.group || !params?.value) return null;
  const dateTime = formatDateTime(params.value);
  if (!dateTime) return null;
  const days = statusStartDaysLabel(params.value);
  return (
    // One inline run inside a block box, so a single ellipsis truncates the
    // date and the day count together when the column is narrowed.
    <Flx ac sx={{ height: "100%", minWidth: 0 }}>
      <Box
        component="span"
        title={days ? `${dateTime} (${days})` : dateTime}
        sx={{
          display: "block",
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {dateTime}
        {days && <span style={{ color: slate[500] }}> ({days})</span>}
      </Box>
    </Flx>
  );
};

export default CellRendererStatusStartDate;
